import React from "react";
import { Brain, Check, X, ArrowRight, RotateCcw, Trophy, Loader2, ChevronRight, Download, Share2 } from "lucide-react";
import { toPng } from "html-to-image";
import api from "../lib/api";
import { Reveal, useSEO } from "../components/site/Reveal";
import { toDataUrl, LOGO } from "../lib/images";
import ReelDownloadButton from "../components/site/ReelDownloadButton";
import Newsletter from "../components/site/Newsletter";
import FullImage from "../components/site/FullImage";

const OPTION_LETTERS = ["A", "B", "C", "D", "E"];

export default function Quiz() {
  useSEO(
    "Yorùbá Culture Quiz (Ìdánwò) — Test Your Knowledge",
    "Test your knowledge of Yorùbá heritage — Òrìṣà, history, language, names and philosophy. Answer one question at a time with instant feedback and explanations."
  );

  const [stage, setStage] = React.useState("categories"); // categories | playing | done
  const [cats, setCats] = React.useState([]);
  const [total, setTotal] = React.useState(0);
  const [loadingCats, setLoadingCats] = React.useState(true);

  const [activeCategory, setActiveCategory] = React.useState(null);
  const [questions, setQuestions] = React.useState([]);
  const [loadingQ, setLoadingQ] = React.useState(false);

  const [idx, setIdx] = React.useState(0);
  const [selected, setSelected] = React.useState(null);
  const [answered, setAnswered] = React.useState(false);
  const [revealed, setRevealed] = React.useState(false);
  const [score, setScore] = React.useState(0);

  const [cardImg, setCardImg] = React.useState(null);
  const [logoImg, setLogoImg] = React.useState(null);
  const [cardBusy, setCardBusy] = React.useState("");
  const cardRef = React.useRef(null);

  React.useEffect(() => {
    toDataUrl(LOGO).then(setLogoImg).catch(() => {});
  }, []);

  React.useEffect(() => {
    api
      .get("/quiz/categories")
      .then((r) => {
        setCats(r.data.categories || []);
        setTotal(r.data.total || 0);
      })
      .catch(() => {})
      .finally(() => setLoadingCats(false));
  }, []);

  const startQuiz = async (category) => {
    setActiveCategory(category);
    setLoadingQ(true);
    setStage("playing");
    try {
      const params = new URLSearchParams();
      if (category && category !== "all") params.set("category", category);
      params.set("limit", 10);
      const r = await api.get(`/quiz/questions?${params.toString()}`);
      setQuestions(r.data || []);
      setIdx(0);
      setSelected(null);
      setAnswered(false);
      setRevealed(false);
      setScore(0);
    } catch {
      setQuestions([]);
    } finally {
      setLoadingQ(false);
    }
  };

  const current = questions[idx];

  const choose = (i) => {
    if (answered) return;
    setSelected(i);
    setAnswered(true);
    if (i === current.correct_index) setScore((s) => s + 1);
  };

  const next = () => {
    if (idx + 1 >= questions.length) {
      setStage("done");
      return;
    }
    setIdx((n) => n + 1);
    setSelected(null);
    setAnswered(false);
    setRevealed(false);
  };

  const reset = () => {
    setStage("categories");
    setActiveCategory(null);
    setQuestions([]);
    setIdx(0);
    setSelected(null);
    setAnswered(false);
    setRevealed(false);
    setScore(0);
  };

  // Render a branded PNG card for the current question, then download or share it.
  const shareQuestion = async (action) => {
    if (!current) return;
    setCardBusy(action);
    setCardImg(current.image ? await toDataUrl(current.image) : null);
    await new Promise((res) => setTimeout(res, 120));
    try {
      if (!cardRef.current) return;
      const url = await toPng(cardRef.current, { pixelRatio: 2, cacheBust: true, skipFonts: true, backgroundColor: "#12100e" });
      if (!url) return;
      if (action === "share") {
        const blob = await (await fetch(url)).blob();
        const file = new File([blob], "yoruba-quiz.png", { type: "image/png" });
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          await navigator.share({ files: [file], title: "Yorùbá Quiz", text: `${current.question} — via ÀsÀNigHT Worldwide` });
          return;
        }
      }
      const a = document.createElement("a");
      a.href = url;
      a.download = "yoruba-quiz.png";
      a.click();
    } catch (e) {
      console.warn("Quiz share card could not be generated or was cancelled:", e);
    } finally {
      setCardBusy("");
    }
  };

  return (
    <div className="bg-night text-cream min-h-screen">
      {/* HERO */}
      <header className="max-w-3xl mx-auto px-6 pt-24 pb-10">
        <Reveal>
          <div className="flex items-center gap-2 mb-4">
            <Brain className="w-4 h-4 text-gold" />
            <p className="uppercase tracking-[0.25em] text-xs text-gold-soft">Yorùbá Quiz · Ìdánwò</p>
          </div>
          <h1 className="font-serif text-5xl lg:text-6xl leading-[1.05] mb-5" data-testid="quiz-title">
            Test Your Knowledge
          </h1>
          <p className="text-cream/70 text-lg leading-relaxed">
            How well do you know Yorùbá heritage? Pick a theme and answer one question at a time —
            you'll see the right answer and a short explanation immediately.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <ReelDownloadButton section="quiz" label="Reel (16:9)" />
            <ReelDownloadButton section="quiz" format="vertical" label="Story (9:16)" />
          </div>
        </Reveal>
      </header>

      <div className="max-w-3xl mx-auto px-6 pb-24">
        {/* CATEGORY PICKER */}
        {stage === "categories" && (
          <div data-testid="quiz-category-picker">
            {loadingCats ? (
              <p className="text-cream/50 py-16 text-center">Loading themes…</p>
            ) : total === 0 ? (
              <p className="text-cream/50 py-16 text-center" data-testid="quiz-empty">
                No quiz questions have been posted yet. Please check back soon!
              </p>
            ) : (
              <div className="space-y-4">
                <Reveal>
                  <button
                    onClick={() => startQuiz("all")}
                    data-testid="quiz-start-all"
                    className="w-full group flex items-center justify-between gap-4 rounded-2xl border border-gold/40 bg-gold/10 p-6 text-left hover:bg-gold/15 transition-colors"
                  >
                    <div>
                      <p className="font-serif text-2xl text-gold">Mixed Challenge</p>
                      <p className="text-cream/65 text-sm mt-1">A little of everything — {total} questions in the pool</p>
                    </div>
                    <ChevronRight className="w-6 h-6 text-gold shrink-0 group-hover:translate-x-1 transition-transform" />
                  </button>
                </Reveal>

                <p className="text-xs uppercase tracking-[0.2em] text-cream/40 pt-4 pb-1">Or pick a theme</p>
                <div className="grid gap-3 sm:grid-cols-2">
                  {cats.map((c, i) => (
                    <Reveal key={c.category} delay={i * 0.04}>
                      <button
                        onClick={() => startQuiz(c.category)}
                        data-testid={`quiz-category-${c.category}`}
                        className="w-full h-full group flex items-center justify-between gap-3 rounded-2xl border border-cream/12 bg-night-surface p-5 text-left hover:border-gold transition-colors"
                      >
                        <div>
                          <p className="font-serif text-xl text-cream group-hover:text-gold transition-colors">{c.category}</p>
                          <p className="text-cream/50 text-sm mt-0.5">{c.count} {c.count === 1 ? "question" : "questions"}</p>
                        </div>
                        <ArrowRight className="w-5 h-5 text-cream/40 shrink-0 group-hover:text-gold group-hover:translate-x-1 transition-all" />
                      </button>
                    </Reveal>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* PLAYING */}
        {stage === "playing" && (
          <div data-testid="quiz-playing">
            {loadingQ ? (
              <p className="text-cream/50 py-16 text-center flex items-center justify-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" /> Preparing your quiz…
              </p>
            ) : questions.length === 0 ? (
              <div className="text-center py-16">
                <p className="text-cream/60 mb-6">No questions found for this theme yet.</p>
                <button onClick={reset} className="inline-flex items-center gap-2 border border-cream/25 text-cream px-5 py-2.5 rounded-full hover:border-gold hover:text-gold transition-colors">
                  <RotateCcw className="w-4 h-4" /> Back to themes
                </button>
              </div>
            ) : (
              <>
                {/* Progress */}
                <div className="flex items-center justify-between mb-4 text-sm">
                  <span className="text-cream/55" data-testid="quiz-progress">
                    Question {idx + 1} of {questions.length}
                  </span>
                  <span className="text-gold font-semibold" data-testid="quiz-running-score">Score: {score}</span>
                </div>
                <div className="h-1.5 w-full rounded-full bg-cream/10 mb-8 overflow-hidden">
                  <div
                    className="h-full bg-gold rounded-full transition-all duration-500"
                    style={{ width: `${((idx + (answered ? 1 : 0)) / questions.length) * 100}%` }}
                  />
                </div>

                {/* Card */}
                <div
                  className={`rounded-2xl border border-cream/12 bg-night-surface p-7 transition-colors ${!revealed ? "cursor-pointer hover:border-gold/50" : ""}`}
                  data-testid="quiz-question-card"
                  onClick={!revealed ? () => setRevealed(true) : undefined}
                  role={!revealed ? "button" : undefined}
                  aria-label={!revealed ? "Reveal answer options" : undefined}
                >
                  <p className="text-[11px] uppercase tracking-[0.2em] text-gold-soft mb-3">{current.category}</p>
                  {current.image && (
                    <FullImage src={current.image} alt="" className="h-72 rounded-xl mb-6" testId="quiz-question-image" />
                  )}
                  <h2 className="font-serif text-2xl md:text-3xl leading-snug mb-7" data-testid="quiz-question-text">
                    {current.question}
                  </h2>

                  {/* Reveal prompt (before answering) */}
                  {!revealed && (
                    <div className="flex flex-col items-center text-center py-2" data-testid="quiz-reveal-prompt">
                      <span className="inline-flex items-center gap-2 bg-gold text-night font-semibold px-6 py-3 rounded-full">
                        <Brain className="w-4 h-4" /> Tap the card to answer
                      </span>
                      <p className="text-cream/45 text-xs mt-3">Think it through, then reveal the {current.options.length} options</p>
                      <button
                        onClick={(e) => { e.stopPropagation(); shareQuestion("download"); }}
                        disabled={cardBusy !== ""}
                        data-testid="quiz-download-question-button"
                        className="mt-5 inline-flex items-center gap-2 border border-cream/25 text-cream text-sm font-semibold px-4 py-2 rounded-full hover:border-gold hover:text-gold transition-colors disabled:opacity-50"
                      >
                        {cardBusy === "download" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                        Download Card
                      </button>
                    </div>
                  )}

                  {revealed && (
                  <div className="space-y-3">
                    {current.options.map((opt, i) => {
                      const isCorrect = i === current.correct_index;
                      const isChosen = i === selected;
                      let cls = "border-cream/20 hover:border-gold hover:bg-cream/5";
                      if (answered) {
                        if (isCorrect) cls = "border-emerald-400 bg-emerald-400/10";
                        else if (isChosen) cls = "border-red-400 bg-red-400/10";
                        else cls = "border-cream/10 opacity-60";
                      }
                      return (
                        <button
                          key={i}
                          onClick={() => choose(i)}
                          disabled={answered}
                          data-testid={`quiz-option-${i}`}
                          className={`w-full flex items-center gap-4 rounded-xl border px-5 py-4 text-left transition-all ${cls} disabled:cursor-default`}
                        >
                          <span className="grid place-items-center min-w-8 h-8 px-1.5 rounded-full border border-current text-sm font-semibold shrink-0 text-gold">
                            {(current.option_labels || [])[i] || OPTION_LETTERS[i]}
                          </span>
                          <span className="flex-1 text-cream">{opt}</span>
                          {answered && isCorrect && <Check className="w-5 h-5 text-emerald-400 shrink-0" />}
                          {answered && isChosen && !isCorrect && <X className="w-5 h-5 text-red-400 shrink-0" />}
                        </button>
                      );
                    })}
                  </div>
                  )}

                  {/* Feedback */}
                  {answered && (
                    <div className="mt-6 rounded-xl bg-night p-5 border border-cream/10" data-testid="quiz-feedback">
                      <p className={`font-semibold mb-1 ${selected === current.correct_index ? "text-emerald-400" : "text-red-400"}`}>
                        {selected === current.correct_index ? "Ẹ kú iṣẹ́! Correct." : "Not quite."}
                      </p>
                      {current.explanation && (
                        <p className="text-cream/70 text-[15px] leading-relaxed">{current.explanation}</p>
                      )}
                      <div className="flex flex-wrap gap-2 mt-4">
                        <button
                          onClick={() => shareQuestion("download")}
                          disabled={cardBusy !== ""}
                          data-testid="quiz-download-card-button"
                          className="inline-flex items-center gap-2 bg-gold text-night text-sm font-semibold px-4 py-2 rounded-full hover:bg-gold-light transition-colors disabled:opacity-50"
                        >
                          {cardBusy === "download" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                          Download Card
                        </button>
                        <button
                          onClick={() => shareQuestion("share")}
                          disabled={cardBusy !== ""}
                          data-testid="quiz-share-card-button"
                          className="inline-flex items-center gap-2 border border-cream/25 text-cream text-sm font-semibold px-4 py-2 rounded-full hover:border-gold hover:text-gold transition-colors disabled:opacity-50"
                        >
                          {cardBusy === "share" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />}
                          Share
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Next */}
                  {answered && (
                    <button
                      onClick={next}
                      data-testid="quiz-next-button"
                      className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-gold text-night font-semibold py-3.5 rounded-full hover:bg-gold-light transition-colors"
                    >
                      {idx + 1 >= questions.length ? "See results" : "Next question"}
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {/* DONE */}
        {stage === "done" && (
          <div className="text-center py-8" data-testid="quiz-results">
            <div className="mx-auto w-20 h-20 grid place-items-center rounded-full bg-gold/15 text-gold mb-6">
              <Trophy className="w-9 h-9" />
            </div>
            <p className="text-xs uppercase tracking-[0.2em] text-gold-soft mb-2">{activeCategory === "all" ? "Mixed Challenge" : activeCategory}</p>
            <h2 className="font-serif text-4xl mb-3" data-testid="quiz-final-score">
              {score} / {questions.length}
            </h2>
            <p className="text-cream/70 text-lg mb-8">
              {score === questions.length
                ? "Àrà! A perfect score — you truly know your roots."
                : score >= questions.length * 0.6
                ? "Well done — your knowledge of Yorùbá heritage is strong."
                : "A great start — every question is a chance to learn more."}
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3">
              <button
                onClick={() => startQuiz(activeCategory)}
                data-testid="quiz-retry-button"
                className="inline-flex items-center gap-2 bg-gold text-night font-semibold px-6 py-3 rounded-full hover:bg-gold-light transition-colors"
              >
                <RotateCcw className="w-4 h-4" /> Play again
              </button>
              <button
                onClick={reset}
                data-testid="quiz-change-theme-button"
                className="inline-flex items-center gap-2 border border-cream/25 text-cream font-semibold px-6 py-3 rounded-full hover:border-gold hover:text-gold transition-colors"
              >
                Choose another theme
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Hidden branded share card (current quiz question) */}
      {current && (
        <div aria-hidden="true" style={{ position: "fixed", top: 0, left: "-20000px", pointerEvents: "none" }}>
          <div
            ref={cardRef}
            data-testid="quiz-share-card"
            style={{
              width: 1080,
              height: 1080,
              background: "linear-gradient(155deg, #1b3327 0%, #12100e 55%)",
              color: "#F5EFE6",
              padding: 90,
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              boxSizing: "border-box",
              position: "relative",
            }}
          >
            <div style={{ position: "absolute", inset: 40, border: "2px solid rgba(212,169,74,0.35)", borderRadius: 28, pointerEvents: "none" }} />

            <div style={{ position: "relative", display: "flex", alignItems: "center", gap: 22 }}>
              {logoImg && (
                <img
                  src={logoImg}
                  alt="ÀsÀNigHT Worldwide"
                  style={{ width: 96, height: 96, borderRadius: "50%", objectFit: "cover", border: "2px solid rgba(212,169,74,0.55)" }}
                />
              )}
              <div>
                <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 46, color: "#D4A94A", lineHeight: 1 }}>
                  ÀsÀNigHT
                </div>
                <div style={{ fontSize: 16, letterSpacing: 8, color: "rgba(245,239,230,0.6)", marginTop: 6 }}>
                  WORLDWIDE
                </div>
              </div>
            </div>
            <div style={{ position: "relative", fontSize: 20, letterSpacing: 4, textTransform: "uppercase", color: "#E8D5A8", marginTop: 40 }}>
              Yorùbá Quiz · {current.category || "Ìdánwò"}
            </div>

            <div style={{ position: "relative", flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", overflow: "hidden" }}>
              {cardImg && (
                <img
                  src={cardImg}
                  alt=""
                  style={{ width: "100%", height: 300, objectFit: "cover", borderRadius: 20, border: "3px solid rgba(212,169,74,0.55)", marginBottom: 34 }}
                />
              )}
              <div
                style={{
                  fontFamily: "'Playfair Display', Georgia, serif",
                  color: "#D4A94A",
                  fontSize: current.question.length > 120 ? 46 : current.question.length > 70 ? 56 : 66,
                  lineHeight: 1.16,
                  marginBottom: 30,
                }}
              >
                {current.question}
              </div>
              <div style={{ fontSize: 18, letterSpacing: 4, textTransform: "uppercase", color: "#E8D5A8", marginBottom: 16 }}>
                {answered ? "Ìdáhùn · Answer" : "Àwọn àṣàyàn · Options"}
              </div>
              {answered ? (
                <div style={{ fontSize: 34, lineHeight: 1.35, color: "#F5EFE6", fontWeight: 600 }}>
                  {current.options[current.correct_index]}
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                  {current.options.map((opt, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: 20 }}>
                      <div style={{ minWidth: 52, height: 52, padding: "0 8px", flexShrink: 0, borderRadius: 26, border: "2px solid rgba(212,169,74,0.6)", color: "#D4A94A", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24, fontWeight: 700 }}>
                        {(current.option_labels || [])[i] || OPTION_LETTERS[i]}
                      </div>
                      <div style={{ fontSize: 30, lineHeight: 1.3, color: "#F5EFE6" }}>{opt}</div>
                    </div>
                  ))}
                </div>
              )}
              {answered && current.explanation && (
                <div style={{ fontSize: 26, lineHeight: 1.42, color: "rgba(245,239,230,0.78)", marginTop: 22 }}>
                  {current.explanation}
                </div>
              )}
            </div>

            <div style={{ position: "relative", borderTop: "1px solid rgba(245,239,230,0.15)", paddingTop: 28, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ fontSize: 26, color: "#D4A94A", letterSpacing: 1 }}>asanightworldwide.com</div>
              <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontStyle: "italic", fontSize: 24, color: "rgba(245,239,230,0.5)" }}>
                Test your Yorùbá knowledge
              </div>
            </div>
          </div>
        </div>
      )}

      <Newsletter />
    </div>
  );
}
